import useCommonApiErrorHandling from '@/hooks/useCommonApiErrorHandling';
import useInsertServiceIdInApiUrl from '@/hooks/useInsertServiceIdInApiUrl';
import { PaymentInfoState } from '@/states/paymentInfoState';
import { PaymentInfoResponse } from '@/types/api/mypage/paymentInfo';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import useSetLoading from '../useSetLoading';

/** 支払情報取得APIのURL */
const apiUrl = ApiUrl.Read.PAYMENT_V2;

/**
 * お支払い情報取得APIの呼び出しフック
 * SigV4署名付きGETリクエストで支払方法（クレジットカード・口座振替等）の情報を取得し、
 * PaymentInfoStateに保存する。エラー時は共通エラーハンドリングを行う。
 *
 * @returns execute - API実行関数
 */
const useGetPayment = () => {
  const { insertServiceIdInApiUrl } = useInsertServiceIdInApiUrl();
  const { startLoading, finishLoading } = useSetLoading();
  const { commonErrorHandling, commonResponseCodeHandling } = useCommonApiErrorHandling();

  const setPaymentInfo = useSetRecoilState(PaymentInfoState);

  const execute = useCallback(async () => {
    // 支払情報も契約サービス単位で取得するため、serviceId付きURLへ変換する。
    const apiUrlWithServiceId = insertServiceIdInApiUrl(apiUrl);
    try {
      startLoading(apiUrlWithServiceId);
      const headers = await getSigV4HeadersForGet(apiUrlWithServiceId);

      const { data }: AxiosResponse<PaymentInfoResponse, any> = await api.get(
        apiUrlWithServiceId,
        headers
      );

      const { isError } = commonResponseCodeHandling({
        responseCode: data.response_code,
        isTransitionTop: true,
      });

      if (isError) return;

      const paymentInfo = data;
      // 後続画面で共通参照できるよう、取得結果をRecoilへ保持する。
      setPaymentInfo(paymentInfo);
    } catch (error: any) {
      // ログイントークンの有効期限が切れていたら、ログイン画面に遷移
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }

      commonErrorHandling({ error, isTransitionTop: true });
    } finally {
      finishLoading(apiUrlWithServiceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { execute };
};

export default useGetPayment;
